import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Save, Loader2, ArrowLeft, User, Mail, Phone, Lock, Calendar, Building2, Briefcase, ShieldCheck, FileText } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import tr from "@/i18n/tr";
import { useCan } from "@/components/common/Can";
import Forbidden from "@/pages/Forbidden";

function Field({ label, Icon, children, error }) {
    return (
        <div className="space-y-1.5">
            <label className="block text-[12.5px] font-semibold text-foreground">{label}</label>
            <div className="relative">
                {Icon && <Icon className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />}
                {children}
            </div>
            {error && <p className="text-[11.5px] text-[hsl(var(--destructive))]">{error}</p>}
        </div>
    );
}

const inputCls = "h-11 w-full rounded-xl border border-border bg-background pl-10 pr-3 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand/40 focus:border-brand/40 transition";
const inputPlain = "h-11 w-full rounded-xl border border-border bg-background px-3.5 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand/40 focus:border-brand/40 transition";

export default function PersonnelNew() {
    const canCreate = useCan("employees.create");
    const navigate = useNavigate();

    const [form, setForm] = useState({
        first_name: "", last_name: "", email: "", phone: "",
        password: "", avatar_url: "",
        birth_date: "", hire_date: new Date().toISOString().slice(0, 10),
        department: "", title: "", role: "employee", status: "active", notes: "",
    });
    const [departments, setDepartments] = useState([]);
    const [roles, setRoles] = useState([]);
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const [d, r] = await Promise.all([
                    api.get("/departments"),
                    api.get("/roles"),
                ]);
                setDepartments(d.data.items || []);
                setRoles(r.data.items || []);
            } catch (_) { /* ignore */ }
        })();
    }, []);

    if (!canCreate) return <Forbidden />;

    const on = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const validate = () => {
        const e = {};
        if (!form.first_name.trim()) e.first_name = tr.validation.required;
        if (!form.last_name.trim()) e.last_name = tr.validation.required;
        if (!form.email.trim()) e.email = tr.validation.required;
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = tr.validation.emailInvalid;
        if (!form.password || form.password.length < 8) e.password = tr.validation.passwordMin;
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const onSubmit = async (ev) => {
        ev.preventDefault();
        if (!validate()) return;
        setSaving(true);
        try {
            const payload = { ...form, email: form.email.trim().toLowerCase() };
            const { data } = await api.post("/employees", payload);
            toast.success(tr.personnel.create.success);
            navigate(`/personnel/${data.id}`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Personel oluşturulamadı.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-6 animate-fade-in">
            <div>
                <Link to="/personnel" className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-muted-foreground hover:text-foreground mb-3">
                    <ArrowLeft className="h-3.5 w-3.5" /> {tr.personnel.title}
                </Link>
                <h1 className="text-[26px] font-bold tracking-tight">{tr.personnel.create.title}</h1>
                <p className="text-[13.5px] text-muted-foreground mt-1">{tr.personnel.create.subtitle}</p>
            </div>

            <form onSubmit={onSubmit} className="space-y-6" data-testid="personnel-new-form">
                {/* Personal */}
                <section className="card-elevated p-6">
                    <div className="mb-4">
                        <div className="text-[15px] font-semibold">{tr.personnel.create.personalInfo}</div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Field label={tr.common.firstName} Icon={User} error={errors.first_name}>
                            <input value={form.first_name} onChange={on("first_name")} className={inputCls} data-testid="input-first-name" />
                        </Field>
                        <Field label={tr.common.lastName} Icon={User} error={errors.last_name}>
                            <input value={form.last_name} onChange={on("last_name")} className={inputCls} data-testid="input-last-name" />
                        </Field>
                        <Field label={tr.common.email} Icon={Mail} error={errors.email}>
                            <input type="email" value={form.email} onChange={on("email")} className={inputCls} placeholder="ornek@ombagency.com" data-testid="input-email" />
                        </Field>
                        <Field label={tr.common.phone} Icon={Phone}>
                            <input value={form.phone} onChange={on("phone")} className={inputCls} placeholder="+90 555 000 00 00" data-testid="input-phone" />
                        </Field>
                        <Field label={tr.common.birthDate} Icon={Calendar}>
                            <input type="date" value={form.birth_date} onChange={on("birth_date")} className={inputCls} data-testid="input-birth" />
                        </Field>
                        <Field label="Profil Fotoğrafı URL" Icon={User}>
                            <input value={form.avatar_url} onChange={on("avatar_url")} className={inputCls} placeholder="https://…" />
                        </Field>
                    </div>
                </section>

                {/* Work info */}
                <section className="card-elevated p-6">
                    <div className="mb-4">
                        <div className="text-[15px] font-semibold">{tr.personnel.create.workInfo}</div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Field label={tr.common.hireDate} Icon={Calendar}>
                            <input type="date" value={form.hire_date?.slice(0, 10) || ""} onChange={on("hire_date")} className={inputCls} data-testid="input-hire-date" />
                        </Field>
                        <Field label={tr.common.department} Icon={Building2}>
                            <select value={form.department} onChange={on("department")} className={inputPlain} data-testid="input-department">
                                <option value="">{tr.common.none}</option>
                                {departments.map((d) => <option key={d.id} value={d.name}>{d.name}</option>)}
                            </select>
                        </Field>
                        <Field label={tr.common.title} Icon={Briefcase}>
                            <input value={form.title} onChange={on("title")} className={inputCls} placeholder="Ör: Sosyal Medya Uzmanı" data-testid="input-title" />
                        </Field>
                        <Field label={tr.common.role} Icon={ShieldCheck}>
                            <select value={form.role} onChange={on("role")} className={inputPlain} data-testid="input-role">
                                {roles.map((r) => <option key={r.name} value={r.name}>{r.display_name}</option>)}
                            </select>
                        </Field>
                        <Field label={tr.common.status} Icon={ShieldCheck}>
                            <select value={form.status} onChange={on("status")} className={inputPlain} data-testid="input-status">
                                <option value="active">{tr.status.active}</option>
                                <option value="inactive">{tr.status.inactive}</option>
                                <option value="on_leave">{tr.status.on_leave}</option>
                            </select>
                        </Field>
                        <div className="md:col-span-2">
                            <Field label={tr.common.notes} Icon={FileText}>
                                <textarea value={form.notes} onChange={on("notes")} rows={3} className="w-full rounded-xl border border-border bg-background pl-10 pr-3 pt-2.5 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand/40 focus:border-brand/40" placeholder="Kişisel not veya yönetici notu…" />
                            </Field>
                        </div>
                    </div>
                </section>

                {/* Security */}
                <section className="card-elevated p-6">
                    <div className="mb-4">
                        <div className="text-[15px] font-semibold">{tr.personnel.create.security}</div>
                        <div className="text-[12.5px] text-muted-foreground mt-0.5">Başlangıç şifresi belirleyin. Personel ilk girişte değiştirebilir.</div>
                    </div>
                    <div className="max-w-md">
                        <Field label={tr.common.password} Icon={Lock} error={errors.password}>
                            <input type="password" value={form.password} onChange={on("password")} className={inputCls} placeholder="En az 8 karakter" data-testid="input-password" />
                        </Field>
                    </div>
                </section>

                <div className="flex justify-end gap-3">
                    <Link to="/personnel" className="inline-flex items-center justify-center rounded-xl border border-border bg-background px-4 py-2.5 text-sm font-semibold text-foreground hover:bg-muted">
                        {tr.common.cancel}
                    </Link>
                    <button type="submit" disabled={saving} className="btn-brand h-11" data-testid="btn-save">
                        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                        {tr.common.save}
                    </button>
                </div>
            </form>
        </div>
    );
}
